import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { IconButton, Box, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Location from './location.svg';
import { requestHeadingPermission, useDeviceHeading } from '@app/shared/hooks/useDeviceHeading';
import { Z_INDEX } from '@app/shared/styles/zIndex';

const LONG_PRESS_DURATION = 300;
const LOCATION_BTN_STORAGE_KEY = 'locationButtonPosition';

interface MapLocationButtonProps {
  mapRef: React.MutableRefObject<any>;
  isTrackingRef: React.MutableRefObject<boolean>;
  locationBtnStateRef: React.MutableRefObject<'user' | 'stops'>;
  getGeolocation: () => { latitude: number | null; longitude: number | null };
  allStops: any[];
  flyToFitStops: (stops: any[], opts?: any) => void;
}

export const MapLocationButton = React.memo(function MapLocationButton({
  mapRef,
  isTrackingRef,
  locationBtnStateRef,
  getGeolocation,
  allStops,
  flyToFitStops,
}: MapLocationButtonProps) {
  const { t } = useTranslation();

  const [barPos, setBarPos] = useState<{ right: number; top: number }>(() => {
    try {
      const saved = localStorage.getItem(LOCATION_BTN_STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch { }
    return { right: 5, top: 60 };
  });
  const [isDraggingBar, setIsDraggingBar] = useState(false);
  const barPressStart = useRef<{ time: number; x: number; y: number } | null>(null);
  const barDragOffset = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);
  const wasDraggingRef = useRef(false);

  // Heading-up toggle. When on, subscribe to device orientation and rotate the map.
  const [headingUp, setHeadingUp] = useState(false);
  const heading = useDeviceHeading(headingUp);

  // Apply the heading to the map (if leaflet-rotate is loaded, setBearing exists)
  useEffect(() => {
    const map = mapRef.current as any;
    if (!map || typeof map.setBearing !== 'function') return;
    if (headingUp && heading !== null) {
      map.setBearing(360 - heading);
    }
  }, [heading, headingUp, mapRef]);

  // When toggled off, snap back to north
  useEffect(() => {
    if (headingUp) return;
    const map = mapRef.current as any;
    if (map && typeof map.setBearing === 'function') {
      map.setBearing(0);
    }
  }, [headingUp, mapRef]);

  useEffect(() => {
    const handleWindowPointerMove = (e: PointerEvent) => {
      if (!isDraggingBar) return;
      e.preventDefault();
      // Bar is portaled to body, so closest() can't find the map container — query it directly.
      const container = document.querySelector('[data-map-container]');
      const containerRect = container?.getBoundingClientRect();
      const barRect = barRef.current?.getBoundingClientRect();
      if (containerRect && barRect) {
        const newRight = containerRect.right - e.clientX - (barRect.width - barDragOffset.current.x);
        const newTop = ((e.clientY - containerRect.top - barDragOffset.current.y) / containerRect.height) * 100;
        const clampedRight = Math.max(5, Math.min(containerRect.width - barRect.width - 5, newRight));
        const clampedTop = Math.max(5, Math.min(90, newTop));
        setBarPos({ right: clampedRight, top: clampedTop });
      }
    };
    const handleWindowPointerUp = () => {
      if (!isDraggingBar) return;
      wasDraggingRef.current = true;
      requestAnimationFrame(() => { wasDraggingRef.current = false; });
      setIsDraggingBar(false);
      barPressStart.current = null;
      if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; }
      localStorage.setItem(LOCATION_BTN_STORAGE_KEY, JSON.stringify(barPos));
    };
    if (isDraggingBar) {
      mapRef.current?.dragging?.disable();
      mapRef.current?.touchZoom?.disable();
      window.addEventListener('pointermove', handleWindowPointerMove);
      window.addEventListener('pointerup', handleWindowPointerUp);
    }
    return () => {
      mapRef.current?.dragging?.enable();
      mapRef.current?.touchZoom?.enable();
      window.removeEventListener('pointermove', handleWindowPointerMove);
      window.removeEventListener('pointerup', handleWindowPointerUp);
    };
  }, [isDraggingBar, barPos, mapRef]);

  const handleBarPointerDown = useCallback((e: React.PointerEvent) => {
    barPressStart.current = { time: Date.now(), x: e.clientX, y: e.clientY };
    const rect = barRef.current?.getBoundingClientRect();
    if (rect) {
      barDragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    }
    longPressTimerRef.current = setTimeout(() => {
      setIsDraggingBar(true);
    }, LONG_PRESS_DURATION);
  }, []);

  const handleBarPointerUp = useCallback((e: React.PointerEvent) => {
    e.stopPropagation();
    if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; }
    barPressStart.current = null;
    if (isDraggingBar) {
      wasDraggingRef.current = true;
      requestAnimationFrame(() => { wasDraggingRef.current = false; });
      setIsDraggingBar(false);
      localStorage.setItem(LOCATION_BTN_STORAGE_KEY, JSON.stringify(barPos));
    }
  }, [isDraggingBar, barPos]);

  const handleBarPointerCancel = useCallback(() => {
    if (isDraggingBar) {
      wasDraggingRef.current = true;
      requestAnimationFrame(() => { wasDraggingRef.current = false; });
    }
    setIsDraggingBar(false);
    barPressStart.current = null;
    if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; }
  }, [isDraggingBar]);

  const handleLocationClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (isDraggingBar || wasDraggingRef.current) return;
    if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; }

    if (mapRef.current) {
      const geolocation = getGeolocation();
      if (geolocation.latitude && geolocation.longitude) {
        if (locationBtnStateRef.current === 'user' && allStops.length > 0) {
          locationBtnStateRef.current = 'stops';
          isTrackingRef.current = false;
          flyToFitStops(allStops, { padding: 50, maxZoom: 18 });
        } else {
          locationBtnStateRef.current = 'user';
          isTrackingRef.current = true;
          mapRef.current.flyTo([geolocation.latitude, geolocation.longitude], 17, { duration: 1.0 });
        }
      }
    }
  }, [isDraggingBar, getGeolocation, allStops, flyToFitStops, mapRef, isTrackingRef, locationBtnStateRef]);

  const handleCompassClick = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isDraggingBar || wasDraggingRef.current) return;
    if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; }

    if (headingUp) {
      setHeadingUp(false);
      return;
    }
    const granted = await requestHeadingPermission();
    if (granted) setHeadingUp(true);
  }, [headingUp, isDraggingBar]);

  const buttonSize = { xs: '40px', md: '48px' };

  return ReactDOM.createPortal(
    <Box
      ref={barRef}
      onPointerDown={handleBarPointerDown}
      onPointerUp={handleBarPointerUp}
      onPointerCancel={handleBarPointerCancel}
      sx={{
        // Portaled to document.body so the bar lives in the root stacking context,
        // unaffected by Leaflet's pane stacking inside .leaflet-container. With
        // position: fixed the right/top values reference the viewport directly.
        position: 'fixed',
        // Map button layer: above bottom-sheet, below content overlays
        // (BigStopPage, modals). See src/shared/styles/zIndex.ts.
        zIndex: Z_INDEX.mapButton,
        bgcolor: 'white',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        overflow: 'hidden',
        borderRadius: '16px',
        border: '1px solid',
        borderColor: isDraggingBar ? 'rgb(208, 85, 46)' : 'rgb(236, 236, 236)',
        boxShadow: isDraggingBar
          ? '0px 4px 12px 0px rgba(208,85,46,0.3)'
          : '0px 0px 6px 0px rgba(0,37,74,0.10)',
        right: `${barPos.right}px`,
        top: `${barPos.top}%`,
        transform: isDraggingBar ? 'scale(1.1)' : 'scale(1)',
        transition: isDraggingBar ? 'none' : 'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
        cursor: isDraggingBar ? 'grabbing' : 'pointer',
        touchAction: 'none',
      }}
    >
      <IconButton
        aria-label={t('accessibility.go_to_my_location') as string}
        onClick={handleLocationClick}
        sx={{
          width: buttonSize,
          minHeight: buttonSize,
          height: buttonSize,
          borderRadius: 0,
          '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
        }}
      >
        <Box component="img" src={Location} alt="" aria-hidden="true" sx={{ pointerEvents: 'none', width: '60%', height: '60%' }} />
      </IconButton>
      <Divider sx={{ borderColor: 'rgb(236, 236, 236)' }} />
      <IconButton
        aria-label={(t('accessibility.toggle_heading_up') as string) || 'Align map to my facing direction'}
        onClick={handleCompassClick}
        sx={{
          width: buttonSize,
          minHeight: buttonSize,
          height: buttonSize,
          borderRadius: 0,
          color: headingUp ? 'rgb(208, 85, 46)' : 'rgba(0,0,0,0.7)',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
        }}
      >
        <i
          className={headingUp ? 'fa-solid fa-location-arrow' : 'fa-regular fa-compass'}
          style={{ fontSize: '1.1rem', pointerEvents: 'none' }}
        />
      </IconButton>
    </Box>,
    document.body,
  );
});
